import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Select,
  DatePicker,
  Tag,
  Typography,
  Space,
  Statistic,
  Modal,
  Form,
  Input,
  message,
  Tabs,
  Calendar,
  Badge,
  Tooltip,
  Avatar,
  Progress
} from 'antd';
import {
  CalendarOutlined,
  UnorderedListOutlined,
  FilterOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  CheckSquareOutlined,
  ContactsOutlined,
  DollarOutlined
} from '@ant-design/icons';
import useTaskStore from '../store/taskStore';
import useAuthStore from '../store/authStore';
import moment from 'moment';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const TasksPage = () => {
  const navigate = useNavigate();
  const {
    tasks,
    stats,
    loading,
    error,
    pagination,
    filters,
    fetchTasks,
    fetchTaskStats,
    updateTaskStatus,
    updateTaskPriority,
    setPage,
    setLimit,
    applyFilters,
    clearFilters
  } = useTaskStore();

  const { user } = useAuthStore();

  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [priorityModalVisible, setPriorityModalVisible] = useState(false);
  const [statusForm] = Form.useForm();
  const [priorityForm] = Form.useForm();

  useEffect(() => {
    fetchTasks();
    fetchTaskStats();
  }, []);

  // Filter tasks based on active tab
  const getFilteredTasks = () => {
    if (activeTab === 'my') {
      return tasks.filter(task => 
        task.assignedTo && task.assignedTo._id === user?._id
      );
    }
    return tasks;
  };

  const filteredTasks = getFilteredTasks();

  // Get stats for my tasks
  const getMyTasksStats = () => {
    const myTasks = tasks.filter(task => 
      task.assignedTo && task.assignedTo._id === user?._id
    );
    
    return {
      totalTasks: myTasks.length,
      pending: myTasks.filter(task => task.status === 'pending').length,
      inProgress: myTasks.filter(task => task.status === 'in_progress').length,
      completed: myTasks.filter(task => task.status === 'completed').length,
      overdue: myTasks.filter(task => 
        moment(task.dueDate).isBefore(moment()) && task.status !== 'completed'
      ).length,
      dueTomorrow: myTasks.filter(task => 
        moment(task.dueDate).format('YYYY-MM-DD') === moment().add(1, 'day').format('YYYY-MM-DD')
      ).length,
      thisWeek: myTasks.filter(task => 
        moment(task.dueDate).isBetween(moment().startOf('week'), moment().endOf('week'))
      ).length
    };
  };

  const displayStats = activeTab === 'my' ? getMyTasksStats() : stats;

  const handleStatusUpdate = async (values) => {
    try {
      await updateTaskStatus(selectedTask.id, values.status, values.notes);
      message.success('Task status updated successfully');
      setStatusModalVisible(false);
      statusForm.resetFields();
      setSelectedTask(null);
      fetchTaskStats(); // Refresh stats
    } catch (error) {
      message.error('Failed to update task status');
    }
  };

  const handlePriorityUpdate = async (values) => {
    try {
      await updateTaskPriority(selectedTask.id, values.priority);
      message.success('Task priority updated successfully');
      setPriorityModalVisible(false);
      priorityForm.resetFields();
      setSelectedTask(null);
      fetchTaskStats(); // Refresh stats
    } catch (error) {
      message.error('Failed to update task priority');
    }
  };

  const openStatusModal = (task) => {
    setSelectedTask(task);
    statusForm.setFieldsValue({
      status: task.status,
      notes: task.notes || ''
    });
    setStatusModalVisible(true);
  };

  const openPriorityModal = (task) => {
    setSelectedTask(task);
    priorityForm.setFieldsValue({
      priority: task.priority
    });
    setPriorityModalVisible(true);
  };

  const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
    const params = {
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
    };

    // Handle table column filters
    if (filtersInfo.priority && filtersInfo.priority.length > 0) {
      params.priority = filtersInfo.priority[0];
    }
    if (filtersInfo.status && filtersInfo.status.length > 0) {
      params.status = filtersInfo.status[0];
    }

    // Handle sorting
    if (sorter.field) {
      params.sort = sorter.field;
      params.order = sorter.order === "ascend" ? "asc" : "desc";
    }

    fetchTasks(params);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <FireOutlined />;
      case 'high': return <ExclamationCircleOutlined />;
      case 'medium': return <ClockCircleOutlined />;
      case 'low': return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getSourceIcon = (sourceType) => {
    return sourceType === 'lead' ? <ContactsOutlined /> : <DollarOutlined />;
  };

  const isOverdue = (dueDate, status) => {
    return moment(dueDate).isBefore(moment()) && status !== 'completed';
  };

  const columns = [
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div>
          <div className="font-medium flex items-center space-x-2">
            <span>{title}</span>
            {isOverdue(record.dueDate, record.status) && (
              <Tag color="red" size="small">OVERDUE</Tag>
            )}
          </div>
          <div className="text-sm text-gray-500 flex items-center space-x-2">
            {getSourceIcon(record.sourceType)}
            <span>{record.sourceName}</span>
            {record.sourceCompany && <span>• {record.sourceCompany}</span>}
          </div>
        </div>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority, record) => (
        <Tag 
          color={getPriorityColor(priority)} 
          icon={getPriorityIcon(priority)}
          style={{ cursor: 'pointer' }}
          onClick={() => openPriorityModal(record)}
        >
          {priority?.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Urgent', value: 'urgent' },
        { text: 'High', value: 'high' },
        { text: 'Medium', value: 'medium' },
        { text: 'Low', value: 'low' }
      ]
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date, record) => (
        <div>
          <div className={isOverdue(date, record.status) ? 'text-red-500 font-medium' : ''}>
            {moment(date).format('MMM DD, YYYY')}
          </div>
          <div className="text-sm text-gray-500">
            {moment(date).format('hh:mm A')}
          </div>
        </div>
      ),
      sorter: (a, b) => moment(a.dueDate) - moment(b.dueDate)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Tag 
          color={getStatusColor(status)}
          style={{ cursor: 'pointer' }}
          onClick={() => openStatusModal(record)}
        >
          {status?.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'In Progress', value: 'in_progress' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' }
      ]
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (assignedTo) => (
        <div className="flex items-center space-x-2">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{assignedTo ? `${assignedTo.firstName} ${assignedTo.lastName}` : 'Unassigned'}</span>
        </div>
      )
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const progress = record.status === 'completed' ? 100 : 
                        record.status === 'in_progress' ? 50 : 
                        record.status === 'cancelled' ? 0 : 10;
        return (
          <Progress 
            percent={progress} 
            size="small" 
            status={record.status === 'cancelled' ? 'exception' : 'normal'}
            strokeColor={record.status === 'completed' ? '#52c41a' : '#1890ff'}
          />
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => openStatusModal(record)}
            icon={<CheckSquareOutlined />}
          >
            Update Status
          </Button>
          <Button
            size="small"
            onClick={() => openPriorityModal(record)}
            icon={<ExclamationCircleOutlined />}
          >
            Priority
          </Button>
        </Space>
      )
    }
  ];

  const getCalendarData = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const dayTasks = filteredTasks.filter(task =>
      moment(task.dueDate).format('YYYY-MM-DD') === dateStr
    );

    return dayTasks.map(task => ({
      type: getStatusColor(task.status),
      content: task.title,
      priority: task.priority
    }));
  };

  const dateCellRender = (value) => {
    const listData = getCalendarData(value);
    return (
      <ul className="events">
        {listData.map((item, index) => (
          <li key={index}>
            <Badge 
              status={item.type} 
              text={
                <span className="flex items-center space-x-1">
                  {getPriorityIcon(item.priority)}
                  <span>{item.content}</span>
                </span>
              } 
            />
          </li>
        ))}
      </ul>
    );
  };

  const handleFilterChange = (key, value) => {
    applyFilters({ [key]: value });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      applyFilters({
        startDate: dates[0].toISOString(),
        endDate: dates[1].toISOString()
      });
    } else {
      applyFilters({
        startDate: null,
        endDate: null
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2">Tasks</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Manage all your tasks from leads and deals
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchTasks();
              fetchTaskStats();
            }}
            loading={loading}
          >
            Refresh
          </Button>
          <Space.Compact>
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
            <Button
              type={viewMode === 'calendar' ? 'primary' : 'default'}
              icon={<CalendarOutlined />}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </Button>
          </Space.Compact>
        </Space>
      </div>

      {/* Tabs */}
      <Card className="shadow-soft">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'all',
              label: (
                <span>
                  <CheckSquareOutlined />
                  All Tasks
                </span>
              )
            },
            {
              key: 'my',
              label: (
                <span>
                  <UserOutlined />
                  My Tasks
                </span>
              )
            }
          ]}
        />
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="Total Tasks"
              value={displayStats?.totalTasks || 0}
              prefix={<CheckSquareOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="Due Tomorrow"
              value={displayStats?.dueTomorrow || displayStats?.totalDueTomorrow || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="Overdue"
              value={displayStats?.overdue || displayStats?.totalOverdue || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="In Progress"
              value={displayStats?.inProgress || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="Completed"
              value={displayStats?.completed || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card className="shadow-soft">
            <Statistic
              title="This Week"
              value={displayStats?.thisWeek || displayStats?.totalThisWeek || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-soft">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Date Range:</Text>
              <RangePicker
                style={{ width: '100%', marginTop: 4 }}
                onChange={handleDateRangeChange}
                value={filters.startDate && filters.endDate ? [
                  moment(filters.startDate),
                  moment(filters.endDate)
                ] : null}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <div>
              <Text strong>Type:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
              >
                <Option value="all">All</Option>
                <Option value="lead">Lead Tasks</Option>
                <Option value="deal">Deal Tasks</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <div>
              <Text strong>Status:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                placeholder="Status"
              >
                <Option value="pending">Pending</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <div>
              <Text strong>Priority:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={filters.priority}
                onChange={(value) => handleFilterChange('priority', value)}
                allowClear
                placeholder="Priority"
              >
                <Option value="urgent">Urgent</Option>
                <Option value="high">High</Option>
                <Option value="medium">Medium</Option>
                <Option value="low">Low</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                clearFilters();
                fetchTasks();
              }}
              style={{ marginTop: 20 }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Content */}
      {viewMode === 'list' ? (
        <Card className="shadow-soft">
          <Table
            columns={columns}
            dataSource={filteredTasks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: activeTab === 'my' ? filteredTasks.length : pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} tasks`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            rowClassName={(record) => 
              `${isOverdue(record.dueDate, record.status) ? 'bg-red-50' : ''} cursor-pointer hover:bg-blue-50`
            }
            onRow={(record) => ({
              onClick: () => navigate(`/tasks/${record.id}`)
            })}
          />
        </Card>
      ) : (
        <Card className="shadow-soft">
          <Calendar
            dateCellRender={dateCellRender}
            onSelect={(date) => {
              const dayTasks = filteredTasks.filter(task =>
                moment(task.dueDate).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
              );
              if (dayTasks.length > 0) {
                Modal.info({
                  title: `Tasks on ${date.format('MMMM DD, YYYY')}`,
                  content: (
                    <div className="space-y-3">
                      {dayTasks.map(task => (
                        <div key={task.id} className="border-l-4 border-blue-500 pl-3">
                          <div className="font-medium flex items-center space-x-2">
                            <span>{task.title}</span>
                            {isOverdue(task.dueDate, task.status) && (
                              <Tag color="red" size="small">OVERDUE</Tag>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {moment(task.dueDate).format('hh:mm A')}
                          </div>
                          <div className="text-sm flex items-center space-x-2">
                            <Tag color={getStatusColor(task.status)}>
                              {task.status?.replace('_', ' ').toUpperCase()}
                            </Tag>
                            <Tag color={getPriorityColor(task.priority)} icon={getPriorityIcon(task.priority)}>
                              {task.priority?.toUpperCase()}
                            </Tag>
                            <span className="ml-2">{task.sourceName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                  width: 600
                });
              }
            }}
          />
        </Card>
      )}

      {/* Status Update Modal */}
      <Modal
        title="Update Task Status"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          statusForm.resetFields();
          setSelectedTask(null);
        }}
        footer={null}
        width={500}
      >
        {selectedTask && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="font-medium">{selectedTask.title}</div>
            <div className="text-sm text-gray-500 mt-1">
              {moment(selectedTask.dueDate).format('MMM DD, YYYY hh:mm A')}
            </div>
            <div className="text-sm text-gray-500">
              {selectedTask.sourceName} • {selectedTask.sourceCompany}
            </div>
          </div>
        )}
        
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusUpdate}
        >
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={4}
              placeholder="Add any notes about this task..."
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setStatusModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Priority Update Modal */}
      <Modal
        title="Update Task Priority"
        open={priorityModalVisible}
        onCancel={() => {
          setPriorityModalVisible(false);
          priorityForm.resetFields();
          setSelectedTask(null);
        }}
        footer={null}
        width={400}
      >
        {selectedTask && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="font-medium">{selectedTask.title}</div>
            <div className="text-sm text-gray-500 mt-1">
              Current Priority: <Tag color={getPriorityColor(selectedTask.priority)} icon={getPriorityIcon(selectedTask.priority)}>
                {selectedTask.priority?.toUpperCase()}
              </Tag>
            </div>
          </div>
        )}
        
        <Form
          form={priorityForm}
          layout="vertical"
          onFinish={handlePriorityUpdate}
        >
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please select a priority' }]}
          >
            <Select>
              <Option value="urgent">
                <span className="flex items-center space-x-2">
                  <FireOutlined style={{ color: '#ff4d4f' }} />
                  <span>Urgent</span>
                </span>
              </Option>
              <Option value="high">
                <span className="flex items-center space-x-2">
                  <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                  <span>High</span>
                </span>
              </Option>
              <Option value="medium">
                <span className="flex items-center space-x-2">
                  <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  <span>Medium</span>
                </span>
              </Option>
              <Option value="low">
                <span className="flex items-center space-x-2">
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Low</span>
                </span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setPriorityModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Priority
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TasksPage;
